import { format } from 'date-fns';

const ISO_WITHOUT_OFFSET = "yyyy-MM-dd'T'HH:mm:ss";
const ISO_OFFSET = 'XXX';

export interface AssignmentDateRange {
  startedOn: string;
  dueOn: string | null;
}

/**
 * Serializes an assignment's start and due dates as ISO 8601 strings that share a single UTC
 * offset, taken from the start date.
 *
 * The GraphQL service rejects a create or update assignment input whose startedOn and dueOn carry
 * different offsets. date-fns `formatISO` derives the offset from each instant separately, so a range that
 * spans a daylight saving transition yields two different offsets (for example -04:00 for a start
 * date in October and -05:00 for a due date in November) and the mutation fails.
 *
 * Anchoring both values to the start date's offset keeps the calendar days the teacher picked
 * while satisfying that validation. The start date is the right anchor because the service also
 * uses startedOn's offset to derive "today in the user's time zone" when validating that neither
 * date is in the past.
 */
export const formatAssignmentDateRange = (startDate: Date, dueDate: Date | null): AssignmentDateRange => {
  const sharedOffset = format(startDate, ISO_OFFSET);

  return {
    startedOn: `${format(startDate, ISO_WITHOUT_OFFSET)}${sharedOffset}`,
    dueOn: dueDate === null ? null : `${format(dueDate, ISO_WITHOUT_OFFSET)}${sharedOffset}`,
  };
};
